<template>
  <div>
    <template v-if="showAccountLink || isAgent">
      <VListItem
        v-for="page in accountMenuList"
        :key="page"
        :to="isAgent ? (validAgentPages.includes(page) ? `/agent/${page}` : '/error') : { name: `account-${pageName(page)}`}"
        :ripple="false"
        active-class="font-weight-medium"
        data-testid="account-link"
        class="text-capitalize"
      >
        {{ isAgent ? agentMenuLabel(page) : $ts(`account.link.${page}`) }}
      </VListItem>
    </template>
    <VDivider v-if="canContextSwitch" />
    <VListItem
      v-if="canContextSwitch"
      data-testid="role-switch"
      :ripple="false"
      @click="switchRole"
    >
      {{ switchRoleText }}
    </VListItem>
    <VDivider />
    <button
      v-if="!isAgent"
      type="button"
      class="v-list-item v-list-item--link text-capitalize logoutbtn"
      :class="{ font14: !mobileMenu }"
      data-testid="auth-logout"
      @click="logout"
    >
      {{ $t('input.btn.signout') }}
    </button>
    <button
      v-if="isAgent && mobileMenu"
      type="button"
      class="v-list-item v-list-item--link text-capitalize logoutbtn"
      data-testid="agent-mobile-logout"
      @click="agentLogout"
    >
      {{ $t('agent.btn.logout') }}
    </button>
  </div>
</template>

<script lang="ts" setup>
// The account links, role switch and sign-out shared by UserMenu's desktop
// dropdown and its mobile drawer, so the two cannot drift apart again (the
// drawer once rendered an empty column for every signed-in user).
import { UserRole } from '~/types/account'
import { Culture } from '~/types/locale'
import { useAuthStore } from '~/stores/auth'
import { useAccountStore } from '~/stores/account'
import { useRootStore } from '~/stores/root'
import { useAgentStore } from '~/stores/agent'
import { usePersistentStore } from '~/stores/persistent'
import { useLocaleStore } from '~/stores/locale'
import { useFeaturesStore } from '~/stores/features'
import { useAccountMenu } from '~/composables/account'
import { useSAuth } from '~/composables/auth'

interface Props {
  mobileMenu?: boolean
}

withDefaults(defineProps<Props>(), {
  mobileMenu: false
})

const rootStore = useRootStore()
const accountStore = useAccountStore()
const authStore = useAuthStore()
const agentStore = useAgentStore()
const persistentStore = usePersistentStore()
const localeStore = useLocaleStore()
const featuresStore = useFeaturesStore()
const $i18n = useI18n()
const accountMenu = useAccountMenu()
const auth = useSAuth()

const isAgent = computed(() => authStore.isAgent)
const pageName = (page: string) => page.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()

// Mirrors nuxt2's UserMenuContent. join-override and payment-override were
// missing here even though both pages exist; release-notes landed in AB#62702.
const BASE_AGENT_PAGES = ['linking', 'features', 'subscriptions', 'orders', 'rewards-adjustment', 'customer-publish', 'join-override', 'payment-override']

// Shown only in the en-us market (business rule AB#60038).
const EN_US_ONLY_AGENT_PAGES = ['release-notes', 'content-search']

// Culture-independent whitelist, so the link still resolves to /agent/<page>.
const validAgentPages = [...BASE_AGENT_PAGES, ...EN_US_ONLY_AGENT_PAGES]

// Only the hyphenated entries need a label; the rest render their raw page name
// through `text-capitalize`, exactly as nuxt2 does.
const AGENT_MENU_LABELS: Record<string, string> = {
  'release-notes': 'Release Notes',
  'content-search': 'Content Search'
}
const agentMenuLabel = (page: string): string => AGENT_MENU_LABELS[page] ?? page

const accountMenuList = computed(() => {
  if (!isAgent.value) {
    return accountMenu.value
  }
  const pages = [...BASE_AGENT_PAGES]
  if (localeStore.culture === Culture.EN_US) {
    if (featuresStore.contentSearchEnabled) {
      pages.push('content-search')
    }
    pages.push('release-notes')
  }
  return pages
})

const logout = async () => {
  await auth.logout()
}

const agentLogout = async () => {
  agentStore.updateViewAsDate(null)
  await persistentStore.updateValue('demoViewAsDate', null)
  await auth.logout()
}

const switchRole = () => {
  accountStore.changeAccountView()
}

const isAuthenticated = computed(() => auth.isAuthenticated.value)

const showAccountLink = computed(() => isAuthenticated.value && (auth.userRole.value === UserRole.Customer || auth.userRole.value === UserRole.Demonstrator))

const switchRoleText = computed(() => auth.isDemo.value
  ? $i18n.t('label.switchToCustomerContext')
  : $i18n.t('label.link.returnToDemonstratorSite')
)

const canContextSwitch = computed(() => isAuthenticated.value && rootStore.isActiveDemo)
</script>

<style lang="scss" scoped>
.logoutbtn {
  width: 100%;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
</style>
